// src/features/inventory/pages/ItemDetailPage/ItemHeader.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, IconButton, Tooltip } from '@mui/material';
import { Check as CheckIcon, ContentCopy as CopyIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';

import PageHeader from '../../../../components/common/PageHeader';
import { ExplainTooltip } from '../../../../components/common/InfoTip';

// Item number leads the subtitle in regular text colour (the rest is grey),
// with a copy button that shows on hover.
function ItemNumber({ sku }) {
  const { t } = useTranslation(['inventory', 'common']);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(sku);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked (non-secure context): the number is still selectable
    }
  };

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        '& .copy-sku': { opacity: copied ? 1 : 0, transition: 'opacity 120ms' },
        '&:hover .copy-sku, & .copy-sku:focus-visible': { opacity: 1 },
      }}
    >
      {t('inventory:items.grid.sku')}
      <Box component="span" data-testid="item-header-sku" sx={{ color: 'text.primary', fontWeight: 600 }}>
        {sku}
      </Box>
      <Tooltip title={copied ? t('inventory:items.detail.sku_copied') : t('common:buttons.copy')}>
        <IconButton className="copy-sku" size="small" onClick={copy} aria-label={t('common:buttons.copy')} sx={{ p: 0.5, my: -1 }}>
          {copied ? <CheckIcon sx={{ fontSize: 14 }} color="success" /> : <CopyIcon sx={{ fontSize: 14 }} />}
        </IconButton>
      </Tooltip>
    </Box>
  );
}

export default function ItemHeader({ currentItem, canUpdate, canDelete, onToggleActive, onEdit, onDelete, onBack, onTabNavigate }) {
  const { t } = useTranslation(['inventory', 'common']);

  const pg = currentItem.product_group;
  const traits = pg ? [
    pg.inventory_tracked && { key: 'inventory_tracked', label: t('inventory:items.detail.inventory_tracked'), color: 'primary', tab: 'stock_log' },
    pg.lot_tracked       && { key: 'lot_tracked',       label: t('inventory:items.detail.lot_tracked'),       color: 'primary', tab: 'batches' },
    pg.serial_tracked    && { key: 'serial_tracked',    label: t('inventory:items.detail.serial_tracked'),    color: 'primary' },
    pg.hazardous         && { key: 'hazardous',         label: t('inventory:items.detail.hazardous'),         color: 'warning', tab: 'hazardous' },
  ].filter(Boolean) : [];

  return (
    <PageHeader
      onBack={onBack}
      title={<span data-testid="item-header-name">{currentItem.name}</span>}
      crumb={currentItem.name}
      badges={<>
        <ExplainTooltip
          title={canUpdate
            ? `${t('inventory:help.flags.active')} ${t('inventory:help.flags.active_toggle')}`
            : t('inventory:help.flags.active')}
        >
          <Chip
            label={currentItem.active ? t('common:status.active') : t('common:status.inactive')}
            color={currentItem.active ? 'success' : 'primary'}
            size="small"
            variant="outlined"
            onClick={canUpdate ? onToggleActive : undefined}
            sx={{ ...(canUpdate && { cursor: 'pointer' }) }}
          />
        </ExplainTooltip>
        {/* Each trait says what it means for the item on hover */}
        {traits.map(({ key, label, color, tab }) => (
          <ExplainTooltip key={key} title={t(`inventory:help.flags.${key}`)}>
            <Chip
              label={label}
              size="small"
              color={color}
              variant="outlined"
              onClick={tab && onTabNavigate ? () => onTabNavigate(tab) : undefined}
              sx={{ ...(tab && onTabNavigate && { cursor: 'pointer' }) }}
            />
          </ExplainTooltip>
        ))}
      </>}
      subtitle={
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1 }}>
          <ItemNumber sku={currentItem.sku} />
          {pg && (
            <span data-testid="item-header-meta">{`· ${pg.code} · ${pg.name}`}</span>
          )}
        </Box>
      }
      actions={<>
        {canDelete && (
          <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={onDelete}>
            {t('common:buttons.delete')}
          </Button>
        )}
        {canUpdate && (
          <Button variant="contained" startIcon={<EditIcon />} onClick={onEdit}>
            {t('common:buttons.edit')}
          </Button>
        )}
      </>}
    />
  );
}
